import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { verdictLabel } from "@/lib/format";
import { AR_SECTOR, AR_SORT, AR_VERDICT, arMap } from "@/i18n_halal";
import { PublicHeader } from "@/components/PublicHeader";
import { OwlLoader } from "@/components/OwlLoader";
import { EmptyState } from "@/components/common";
import {
  ArrowLeft, ArrowRight, Search, ShieldCheck, AlertTriangle, Ban, Filter, Loader2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE = 50;

const TONE = {
  "Compliant": { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: ShieldCheck },
  "Questionable": { cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: AlertTriangle },
  "Non-Compliant": { cls: "bg-rose-50 text-rose-700 border-rose-200", Icon: Ban },
};

const cap = (v) => {
  if (!v) return "—";
  if (v >= 1e12) return `$${(v / 1e12).toFixed(2)}T`;
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(0)}M`;
  return `$${v.toFixed(0)}`;
};

const Verdict = ({ status, label }) => {
  const t = TONE[status] || TONE.Questionable;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap ${t.cls}`}>
      <t.Icon className="h-3 w-3" />{label}
    </span>
  );
};

export default function HalalScreener() {
  const navigate = useNavigate();
  const { t, lang } = useApp();
  const ar = lang === "ar";
  const [params, setParams] = useSearchParams();
  const [facets, setFacets] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");

  const vlabel = useCallback(
    (s) => (ar ? arMap(AR_VERDICT, s) : verdictLabel(s)), [ar]);
  const slabel = useCallback((s) => (ar ? arMap(AR_SECTOR, s) : s), [ar]);

  const filters = useMemo(() => ({
    q: params.get("q") || "",
    market: params.get("market") || "All",
    sector: params.get("sector") || "All",
    verdict: params.get("verdict") || "Compliant",
    sort: params.get("sort") || "market_cap",
    offset: Number(params.get("offset") || 0),
  }), [params]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value && value !== "All") next.set(key, value); else next.delete(key);
    if (key !== "offset") next.delete("offset");
    setParams(next);
  };

  useEffect(() => {
    axios.get(`${API}/public/halal-screener/facets`).then((r) => setFacets(r.data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/public/halal-screener`, {
        params: { ...filters, limit: PAGE },
      });
      setData(d);
    } finally {
      setLoading(false);
    }
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const submitSearch = (e) => { e.preventDefault(); setFilter("q", q.trim()); };
  const page = Math.floor(filters.offset / PAGE) + 1;
  const pages = data ? Math.max(1, Math.ceil(data.total / PAGE)) : 1;
  const ALL_LABEL = { verdict: t("scr_all_verdicts"), market: t("scr_all_markets"), sector: t("scr_all_sectors") };

  return (
    <div className="min-h-screen bg-background" data-testid="halal-screener-page">
      <PublicHeader />
      <div className="navy-strip text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10 sm:py-14">
          <div className="mb-7 flex items-center justify-between gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/")}
              className="text-white/70 hover:text-white hover:bg-white/10" data-testid="screener-back">
              <ArrowLeft className="h-4 w-4 me-1.5" />{t("hl_back")}
            </Button>
          </div>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-[color:var(--tharwy-lime)]">
            <ShieldCheck className="h-3.5 w-3.5" />{t("scr_eyebrow")}
          </div>
          <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight" data-testid="screener-title">
            {t("scr_h1a")}<br />
            <span className="text-white/70">{t("scr_h1b")}</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base text-white/70 leading-relaxed">{t("scr_sub")}</p>

          {facets && (
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="screener-stats">
              {[[t("scr_universe"), facets.universe.toLocaleString()],
                [t("scr_compliant"), facets.counts.Compliant.toLocaleString()],
                [t("scr_questionable"), facets.counts.Questionable.toLocaleString()],
                [t("scr_noncompliant"), facets.counts["Non-Compliant"].toLocaleString()]].map(([k, v]) => (
                <div key={k} className="rounded-2xl bg-white/5 border border-white/10 p-4">
                  <div className="text-[10px] uppercase tracking-[0.14em] text-white/45">{k}</div>
                  <div className="mt-1 numeric text-xl font-bold">{v}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8 sm:py-12">
        {/* filters */}
        <div className="flex flex-wrap items-center gap-2.5" data-testid="screener-filters">
          <form onSubmit={submitSearch} className="relative flex-1 min-w-[220px]">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("scr_search")}
              className="ps-9 h-10" data-testid="screener-search" />
          </form>
          {[["verdict", filters.verdict, ["All", ...(facets?.verdicts || [])]],
            ["market", filters.market, ["All", ...(facets?.markets || [])]],
            ["sector", filters.sector, ["All", ...(facets?.sectors || [])]]].map(([key, value, options]) => (
            <Select key={key} value={value} onValueChange={(v) => setFilter(key, v)}>
              <SelectTrigger className="h-10 w-[150px] capitalize" data-testid={`screener-${key}`}>
                <SelectValue placeholder={ALL_LABEL[key]} />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {options.map((o) => (
                  <SelectItem key={o} value={o}>
                    {o === "All" ? ALL_LABEL[key] : key === "sector" ? slabel(o) : vlabel(o)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ))}
          <Select value={filters.sort} onValueChange={(v) => setFilter("sort", v)}>
            <SelectTrigger className="h-10 w-[160px]" data-testid="screener-sort">
              <SelectValue placeholder={t("scr_sort")} />
            </SelectTrigger>
            <SelectContent>
              {(facets?.sorts || []).map((s) => (
                <SelectItem key={s.key} value={s.key}>{ar ? arMap(AR_SORT, s.label) : s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground" data-testid="screener-count">
          <Filter className="h-3.5 w-3.5" />
          {loading ? t("scr_screening")
            : `${data?.total?.toLocaleString() || 0} ${t("scr_matches")} · ${data?.compliant_pct || 0}% ${t("scr_pct_compliant")}`}
        </div>

        {/* table */}
        <div className="mt-4 rounded-2xl border border-border overflow-hidden bg-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[820px]" data-testid="screener-table">
              <thead className="bg-muted/50 text-muted-foreground">
                <tr className="text-start">
                  <th className="text-start font-semibold px-4 py-3">{t("scr_company")}</th>
                  <th className="text-start font-semibold px-3 py-3">{t("scr_verdict")}</th>
                  <th className="text-end font-semibold px-3 py-3">{t("scr_price")}</th>
                  <th className="text-end font-semibold px-3 py-3">{t("scr_24h")}</th>
                  <th className="text-end font-semibold px-3 py-3">{t("scr_debt")}</th>
                  <th className="text-end font-semibold px-3 py-3">{t("scr_impure")}</th>
                  <th className="text-end font-semibold px-4 py-3">{t("scr_mcap")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {(data?.items || []).map((r) => (
                  <tr key={r.symbol} className="hover:bg-muted/40 cursor-pointer transition-colors"
                    onClick={() => navigate(`/halal-stocks/${encodeURIComponent(r.symbol)}`)}
                    data-testid={`screener-row-${r.symbol}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{r.symbol}</div>
                      <div className="text-xs text-muted-foreground truncate max-w-[220px]">{r.name}</div>
                      <div className="text-[10px] text-muted-foreground">{r.market} · {slabel(r.sector)}</div>
                    </td>
                    <td className="px-3 py-3"><Verdict status={r.status} label={vlabel(r.status)} /></td>
                    <td className="px-3 py-3 text-end numeric whitespace-nowrap">
                      {r.price?.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      <span className="text-[10px] text-muted-foreground ms-1">{r.currency}</span>
                    </td>
                    <td className={`px-3 py-3 text-end numeric ${r.change_pct >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                      {r.change_pct >= 0 ? "+" : ""}{r.change_pct}%
                    </td>
                    <td className="px-3 py-3 text-end numeric">{r.debt_ratio ?? "—"}%</td>
                    <td className="px-3 py-3 text-end numeric">{r.interest_income_pct ?? "—"}%</td>
                    <td className="px-4 py-3 text-end numeric">{cap(r.market_cap_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {loading && (
            <div className="p-6 flex items-center justify-center text-sm text-muted-foreground">
              <OwlLoader size="sm" label={t("scr_loading")} />
            </div>
          )}
          {!loading && data?.items?.length === 0 && (
            <div className="p-8 flex justify-center" data-testid="screener-empty">
              <EmptyState title={t("scr_empty")} />
            </div>
          )}
        </div>

        {/* pagination */}
        <div className="mt-5 flex items-center justify-between">
          <span className="text-xs text-muted-foreground">{t("scr_page")} {page} {t("scr_of")} {pages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={filters.offset === 0}
              onClick={() => setFilter("offset", String(Math.max(0, filters.offset - PAGE)))}
              data-testid="screener-prev">{t("scr_prev")}</Button>
            <Button variant="outline" size="sm" disabled={page >= pages}
              onClick={() => setFilter("offset", String(filters.offset + PAGE))}
              data-testid="screener-next">{t("scr_next")}</Button>
          </div>
        </div>

        <div className="mt-12 rounded-3xl navy-strip text-white p-8 sm:p-10 text-center relative overflow-hidden">
          <div className="absolute inset-0 hero-sheen opacity-50" />
          <div className="relative">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{t("scr_cta_title")}</h2>
            <p className="mt-3 text-white/70 max-w-xl mx-auto">{t("scr_cta_sub")}</p>
            <Button className="mt-6 rounded-full px-8 h-11" onClick={() => navigate("/login")}
              data-testid="screener-cta">{t("hl_open_account")} <ArrowRight className="h-4 w-4 ms-1.5" /></Button>
          </div>
        </div>

        <p className="mt-8 text-xs text-muted-foreground leading-relaxed">
          {t("scr_footnote")}
          <button onClick={() => navigate("/shariah-board")} className="ms-1 text-[#3E2E65] font-semibold"
            data-testid="screener-board-link">{t("hl_read_methodology")}</button>.
        </p>
      </div>
    </div>
  );
}
